import React, { useContext } from "react";
import { Route as RouterRoute, Redirect } from "react-router-dom";

import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";

const routePermissions = {
  "/": "dashboard",
  "/tickets/:ticketId?": "tickets",
  "/connections": "connections",
  "/contacts": "contacts",
  "/users": "users",
  "/quickAnswers": "quickAnswers",
  "/reminders": "reminders",
  "/notes": "notes",
  "/appointments": "agenda",
  "/orders": "orders",
  "/processes": "processes",
  "/streaming": "streaming",
  "/campaigns": "campaigns",
  "/webhooks": "webhooks",
  "/internal-chat": "internalChat",
  "/funnels": "funnels",
  "/chatbot": "chatbot",
  "/ai": "ai",
  "/apps": "sidepanelApps",
  "/reseller": "reseller",
  "/workspace": "workspaceData",
  "/Settings": "settings",
  "/settings": "settings",
  "/settings/ai-logs": "settings",
  "/Queues": "queues",
  "/queues": "queues",
  "/labels": "labels",
  "/billing": "billing",
  "/superadmin": "superadmin",
  "/menu": "menu",
  "/menu-embed/:itemId": "menu"
};

const resellerAllowedPermissions = new Set([
  "connections",
  "chatbot",
  "agenda",
  "orders",
  "processes",
  "streaming",
  "contacts",
  "notes",
  "workspaceData",
  "reseller"
]);

const defaultPermissionPaths = [
  { permission: "dashboard", path: "/" },
  { permission: "connections", path: "/connections" },
  { permission: "tickets", path: "/tickets" },
  { permission: "contacts", path: "/contacts" },
  { permission: "quickAnswers", path: "/quickAnswers" },
  { permission: "reminders", path: "/reminders" },
  { permission: "notes", path: "/notes" },
  { permission: "agenda", path: "/appointments" },
  { permission: "orders", path: "/orders" },
  { permission: "processes", path: "/processes" },
  { permission: "streaming", path: "/streaming" },
  { permission: "campaigns", path: "/campaigns" },
  { permission: "webhooks", path: "/webhooks" },
  { permission: "internalChat", path: "/internal-chat" },
  { permission: "funnels", path: "/funnels" },
  { permission: "chatbot", path: "/chatbot" },
  { permission: "ai", path: "/ai" },
  { permission: "sidepanelApps", path: "/apps" },
  { permission: "reseller", path: "/reseller" },
  { permission: "users", path: "/users" },
  { permission: "queues", path: "/queues" },
  { permission: "labels", path: "/labels" },
  { permission: "billing", path: "/billing" }
];

const adminOnlyPermissions = ["users", "queues", "labels", "webhooks", "billing"];

const roleCanUsePermission = (user, permission) => {
  if (!adminOnlyPermissions.includes(permission)) return true;
  return ["admin", "superadmin", "owner"].includes(user?.profile);
};

const hasMenuPermission = (user, permission) => {
  if (!permission || ["superadmin", "owner"].includes(user?.profile)) return true;
  if (permission === "settings") return false;
  if (!roleCanUsePermission(user, permission)) return false;

  let companyPermissions = user?.company?.menuPermissions;
  let userPermissions = user?.menuPermissions;

  if (typeof companyPermissions === "string" && companyPermissions.trim()) {
    try {
      companyPermissions = JSON.parse(companyPermissions);
    } catch {
      companyPermissions = null;
    }
  }

  if (typeof userPermissions === "string" && userPermissions.trim()) {
    try {
      userPermissions = JSON.parse(userPermissions);
    } catch {
      userPermissions = null;
    }
  }

  if (
    permission === "reseller" &&
    (!Array.isArray(companyPermissions) || !companyPermissions.includes(permission))
  ) {
    return false;
  }

  if (Array.isArray(companyPermissions) && !companyPermissions.includes(permission)) {
    if (permission === "agenda" && !Array.isArray(userPermissions)) return true;
    return false;
  }

  if (!Array.isArray(userPermissions)) {
    return true;
  }

  return userPermissions.includes(permission);
};

const getFallbackPath = user => {
  let userPermissions = user?.menuPermissions;
  let companyPermissions = user?.company?.menuPermissions;

  if (typeof userPermissions === "string" && userPermissions.trim()) {
    try {
      userPermissions = JSON.parse(userPermissions);
    } catch {
      userPermissions = null;
    }
  }

  if (typeof companyPermissions === "string" && companyPermissions.trim()) {
    try {
      companyPermissions = JSON.parse(companyPermissions);
    } catch {
      companyPermissions = null;
    }
  }

  const permissions = Array.isArray(userPermissions)
    ? userPermissions
    : Array.isArray(companyPermissions)
      ? companyPermissions
      : null;

  if (!Array.isArray(permissions)) return "/";

  return (
    defaultPermissionPaths.find(item => hasMenuPermission(user, item.permission))?.path ||
    "/login"
  );
};

const hasResellerBranding = user => Boolean(user?.company?.resellerBranding);

const canAccessResellerRoute = (user, permission) => {
  if (!hasResellerBranding(user)) return true;
  if (!permission) return true;
  return resellerAllowedPermissions.has(permission);
};

const Route = ({
  component: Component,
  isPrivate = false,
  allowAuthenticated = false,
  skipPermission = false,
  ...rest
}) => {
  const { isAuth, loading, user } = useContext(AuthContext);

  if (loading) {
    return <BackdropLoading />;
  }

  if (!isAuth && isPrivate) {
    return (
      <>
        <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
      </>
    );
  }

  if (isAuth && !isPrivate && !allowAuthenticated) {
    return (
      <>
        <Redirect to={{ pathname: "/", state: { from: rest.location } }} />;
      </>
    );
  }

  const routePermission = routePermissions[rest.path];
  if (isAuth && isPrivate && !skipPermission && !canAccessResellerRoute(user, routePermission)) {
    return (
      <>
        <Redirect to={{ pathname: "/connections" }} />
      </>
    );
  }

  if (isAuth && isPrivate && !skipPermission && !hasMenuPermission(user, routePermission)) {
    return (
      <>
        <Redirect to={{ pathname: getFallbackPath(user) }} />
      </>
    );
  }

  return (
    <>
      <RouterRoute {...rest} component={Component} />
    </>
  );
};

export default Route;

